import { Account, AccountBalance, Program } from '@monorepo/types';
import { CalculatedStatistics } from '../types/dashboard-context.types';

/**
 * Statistics Calculator Utilities
 * Functions to calculate derived statistics from raw data
 */

/**
 * Calculate all statistics from account, balance, and program data
 *
 * @param account - Account data
 * @param accountBalance - Account balance data
 * @param program - Program configuration
 * @returns Calculated statistics or null if insufficient data
 */
export const calculateStatistics = (
  account: Account | null,
  accountBalance: AccountBalance | null,
  _program: Program | null,
): CalculatedStatistics | null => {
  if (!account || !accountBalance) {
    return null;
  }

  try {
    return {
      weeklyPerformancePercent:
        calculateWeeklyPerformancePercent(accountBalance),
      pnlFromHwm: calculatePnlFromHwm(accountBalance),
      growthPercentage: calculateGrowthPercentage(accountBalance),
      currentEquity: accountBalance.equity || 0,
      initialBalance: accountBalance.balance || 0,
      highWaterMark: accountBalance.highWaterMark || 0,
    };
  } catch (error) {
    console.error('Error calculating statistics:', error);
    return null;
  }
};

/**
 * Calculate weekly performance percentage
 *
 * @param accountBalance - Account balance data
 * @returns Weekly performance percentage
 */
export const calculateWeeklyPerformancePercent = (
  accountBalance: AccountBalance,
): number => {
  const weeklyPnl = accountBalance.weeklyProfitAndLoss || 0;
  const currentBalance = accountBalance.balance || 0;

  if (currentBalance <= 0) return 0;

  // Calculate the starting balance for the week
  const weekStartBalance = currentBalance - weeklyPnl;

  if (weekStartBalance <= 0) return 0;

  // Calculate percentage change
  return (weeklyPnl / weekStartBalance) * 100;
};

/**
 * Calculate P&L from high water mark
 *
 * @param accountBalance - Account balance data
 * @returns P&L from high water mark
 */
export const calculatePnlFromHwm = (accountBalance: AccountBalance): number => {
  return accountBalance.highWaterMarkPnl || 0;
};

/**
 * Calculate growth percentage
 *
 * @param accountBalance - Account balance data
 * @returns Growth percentage
 */
export const calculateGrowthPercentage = (
  accountBalance: AccountBalance,
): number => {
  return accountBalance.growthPercentage || 0;
};

/**
 * Validate if data is sufficient for statistics calculation
 *
 * @param account - Account data
 * @param accountBalance - Account balance data
 * @param program - Program configuration
 * @returns boolean indicating if data is sufficient
 */
export const isDataSufficientForCalculation = (
  account: Account | null,
  accountBalance: AccountBalance | null,
  _program: Program | null,
): boolean => {
  return !!(
    account &&
    accountBalance &&
    typeof accountBalance.balance === 'number' &&
    typeof accountBalance.equity === 'number'
  );
};

/**
 * Get default statistics when data is insufficient
 *
 * @returns Default statistics with zero values
 */
export const getDefaultStatistics = (): CalculatedStatistics => {
  return {
    weeklyPerformancePercent: 0,
    pnlFromHwm: 0,
    growthPercentage: 0,
    currentEquity: 0,
    initialBalance: 0,
    highWaterMark: 0,
  };
};
